import TaggableTextarea from './TaggableTextarea';
import CommentTagParser from '../parsers/CommentTagParser';

class CommentTextarea extends TaggableTextarea {
    static observedAttributes = ['disabled'];

    attributeChangedCallback(attrName, oldValue, newValue) {
        super.dynamicRender();
    }

    connectedCallback() {
        if (this._commentInitialised) return;
        this._commentInitialised = true;
        super.connectedCallback();

        this.userList = [];

        const usersRaw = this.getAttribute('user-list') ?? '[]';
        const users = JSON.parse(usersRaw);

        if (users) {
            this.userList = users;
        }

        this.setupEvents();
    }

    setupEvents() {
        this.addEventListener('keydown', e => {
            if (this._wrapper.querySelector('.placeholder')) {
                this._placeholder.remove();
            }

            if (this.popupShowing) {
                if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' ||
                    e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        });

        this.addEventListener('keyup', e => {
            if (e.key.length !== 1 && e.key !== 'Backspace' && e.key !== 'Enter' &&
                e.key !== 'Escape' && e.key !== 'Tab' && e.key !== 'ArrowUp' &&
                e.key !== 'ArrowDown') return;

            if (e.key === '@' && this.userList.length > 0) {
                if (isSafari) {
                    alert('Comment tagging is not supported by this browser. Please use Chrome or Firefox to use this feature.');
                    return;
                }

                if (this.popupShowing) {
                    this.hideUserPopup();
                }

                const cursorPos = this.caretCoordinates();
                const hostPos = this.shadowRoot.host.getBoundingClientRect();

                this.showUserPopup(cursorPos.left - hostPos.left, (cursorPos.top - hostPos.top) + 30);

            } else if (this.popupShowing) {
                if (e.key === 'Backspace') {
                    if (this.popupPartialText === '') {
                        this.hideUserPopup();
                        return;
                    } else {
                        this.popupPartialText = this.popupPartialText.substring(0, this.popupPartialText.length - 1);
                        this.populatePopupWithQuery(this.popupPartialText);
                    }

                } else if (e.key === 'Enter' || e.key === 'Tab') {
                    // Select item
                    const items = this.itemsForQuery(this.popupPartialText);

                    if (items.length > 0 && this.popupSelectedIndex >= 0 && this.popupSelectedIndex < items.length) {
                        this.tagUserAtCurrentPosition(items[this.popupSelectedIndex]);
                    }

                    this.hideUserPopup();

                } else if (e.key === 'ArrowUp') {
                    let newIndex = this.popupSelectedIndex - 1;
                    if (newIndex < 0) newIndex = 0;
                    this.highlightPopupItemAtIndex(newIndex);

                } else if (e.key === 'ArrowDown') {
                    const users = this.itemsForQuery(this.popupPartialText);
                    let newIndex = this.popupSelectedIndex + 1;
                    if (newIndex >= users.length) newIndex = users.length - 1;
                    this.highlightPopupItemAtIndex(newIndex);

                } else if (e.key === 'Escape') {
                    this.hideUserPopup();
                } else {
                    this.popupPartialText += e.key;
                    this.populatePopupWithQuery(this.popupPartialText);
                }
            }
        });
    }

    showUserPopup(xPos, yPos) {
        this.popupShowing = true;
        this.popupPartialText = '';

        const selection = document.getSelection();
        const caretInfo = this.getCaretPositionFromSelection(selection);
        this.popupAbsoluteOffset = caretInfo.absoluteLength;
        this.popupNodeIndex = caretInfo.nodeIndex;

        this.popup = document.createElement('div');
        this.popup.classList.add('comment-textarea-popup');
        this.populatePopupWithQuery('');
        this.shadowRoot.appendChild(this.popup);

        this.popup.style.left = xPos + 'px';
        this.popup.style.top = yPos + 'px';
    }

    hideUserPopup() {
        this.popupShowing = false;
        this.popupPartialText = '';
        this.popup.remove();
        this.popup = null;

        // Clean up errant popups
        this.shadowRoot.querySelectorAll('.comment-textarea-popup').forEach((obj) => {
            obj.remove();
        });
    }

    populatePopupWithQuery(query) {
        this.popup.innerHTML = '';

        const items =this.itemsForQuery(query);

        this.popupSelectedIndex = (items.length > 0 ? 0 : -1);

        for (let i = 0; i < items.length; i++) {
           this.popup.innerHTML += `<div class="popup-item` + (i === 0 ? ' selected' : '') + `" data-id="${items[i].id}">${items[i].name}</div>`;
        }

        this.popup.querySelectorAll('.popup-item').forEach((obj, i) => {
            obj.addEventListener('mouseover', e => {
                this.popupSelectedIndex = i;
                this.clearPopupSelection();
                obj.classList.add('selected');
            });

            obj.addEventListener('click', e => {
                const users = this.itemsForQuery(this.popupPartialText);

                if (this.popupSelectedIndex >= 0 && this.popupSelectedIndex < users.length) {
                    this.tagUserAtCurrentPosition(users[this.popupSelectedIndex]);
                }

                this.hideUserPopup();
            });

        });
    }

    highlightPopupItemAtIndex(index) {
        this.clearPopupSelection();

        const items = this.popup.querySelectorAll('.popup-item');
        if (index >= 0 && index < items.length) {
            items[index].classList.add('selected');
            this.popupSelectedIndex = index;
        }

    }

    clearPopupSelection() {
        this.popup.querySelectorAll('.popup-item').forEach((obj) => {
            obj.classList.remove('selected');
        });
    }

    itemsForQuery(query) {
        const items = [];

        this.userList.every((obj) => {
            if (obj.name.toLowerCase().startsWith(query.toLowerCase())) {
                items.push(obj);
                return items.length < 5; // Limit to 5 items
            }
            return true;
        });

        return items;
    }

    caretCoordinates() {
        // Backup html
        const oldHTML = this._wrapper.innerHTML;

        const selection = document.getSelection();
        const offset = selection.focusOffset;

        const caretInfo = this.getCaretPositionFromSelection(selection);

        // Insert span at caret position
        const newHTML = oldHTML.substring(0, caretInfo.absoluteLength) + '<span class="caret-pos"></span>' +
            oldHTML.substring(caretInfo.absoluteLength);

        this._wrapper.innerHTML = newHTML;

        // Force layout recalculation
        this._wrapper.offsetHeight;

        // Get span position
        const span = this._wrapper.querySelector('.caret-pos');

        const rect = span.getClientRects();

        // Restore the original HTML
        this._wrapper.innerHTML = oldHTML;

        // Restore the caret position
        const range = document.createRange();
        const nodes = this._wrapper.childNodes;

        let remainingOffset = caretInfo.absoluteLength;
        let nodeIndex = 0;

        // Find the correct node and offset
        for (let i = 0; i < nodes.length; i++) {
            const nodeLength = nodes[i].outerHTML ? nodes[i].outerHTML.length : nodes[i].textContent.length;

            if (remainingOffset <= nodeLength) {
                nodeIndex = i;
                break;
            }

            remainingOffset -= nodeLength;
        }

        const targetNode = nodes[nodeIndex];
        range.setStart(targetNode, remainingOffset);

        selection.removeAllRanges();
        selection.addRange(range);

        return rect[0];
    }

    tagUserAtCurrentPosition(user) {
        let html = this._wrapper.innerHTML;
        html = html.replaceAll('&nbsp;', ' ');

        const selection = document.getSelection();

        // Insert span at caret position
        this._wrapper.innerHTML = html.substring(0, this.popupAbsoluteOffset - 1) + `<span class="user-tag" contentEditable="false" data-user-id="${user.id}">@${user.name}</span>` +
            '\u00A0' + html.substring(this.popupAbsoluteOffset + this.popupPartialText.length);
        
        // Force layout recalculation
        this._wrapper.offsetHeight;

        // Restore focus and caret position after paint
        window.requestAnimationFrame(() => {
            this._wrapper.focus();
            this.shadowRoot.host.focus();
            selection.removeAllRanges();

            const range = document.createRange();

            // Account for situation where the first character is a tag
            if (this.popupNodeIndex + 2 > this._wrapper.childNodes.length - 1) {
                const textNode = document.createTextNode('\u00A0'); // Non-breaking space
                this._wrapper.appendChild(textNode);

                // Force layout recalculation
                this._wrapper.offsetHeight;

                const node = this._wrapper.childNodes[this.popupNodeIndex + 1];
                if (!node || node.textContent === '') node.textContent = '\u00A0'; // Non-breaking space
                range.setStart(node, 1);
            } else {
                const node = this._wrapper.childNodes[this.popupNodeIndex + 2];
                if (!node || node.textContent === '') node.textContent = '\u00A0'; // Non-breaking space
                range.setStart(node, 1);
            }

            selection.addRange(range);
        });
    }

    getCaretPositionFromSelection(selection) {
        let absoluteLength = 0;
        let nodeIndex = 0;

        // Determine node offset
        const nodes = this._wrapper.childNodes;

        for (let i = 0; i < nodes.length; i++) {
            if (selection.focusNode !== null && selection.focusNode.isSameNode(nodes[i])) {
                absoluteLength += selection.focusOffset;
                nodeIndex = i;
                break;
            } else {
                if (nodes[i].outerHTML) {
                    absoluteLength += nodes[i].outerHTML.length;
                } else {
                    absoluteLength += nodes[i].textContent.length;
                }
            }
        }

        return {
            absoluteLength: absoluteLength,
            nodeIndex: nodeIndex
        };
    }

    triggerFocus() {
        this._wrapper.focus();

        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(this._wrapper);
        range.collapse(this._wrapper.textContent.trim() === '');
        sel.removeAllRanges();
        sel.addRange(range);
    }

    get value() {
        if (this._wrapper.querySelector('.placeholder')) {
            return '';
        } else if (this._wrapper.innerHTML.trim() === '<br>') {
            return '';
        } else {
            return CommentTagParser.convertElementsToTags(this._wrapper.innerHTML).trim();
        }
    }

    set value(text) {
        this._wrapper.innerHTML = CommentTagParser.convertTagsToElements(text).trim();
    }

    isShowingUserPopup() {
        return this.popupShowing;
    }
}

customElements.define('comment-textarea', CommentTextarea);
